import type { Post } from '@/types/posts'
import type { Thread } from '@/types/thread'

/** Build an updater that optimistically inserts a reply into a thread cache entry. */
export function buildThreadReplyUpdater(
    parentUri: string,
    reply: Post
): (old: Thread | undefined) => Thread | undefined {
    return old => {
        if (!old) return old
        const threadUris = new Set([
            old.post.post.uri,
            ...old.parentChain.map(r => r.post.uri),
            ...old.replies.map(r => r.post.uri),
        ])
        if (!threadUris.has(parentUri)) return old
        const parentInReplies = old.replies.find(r => r.post.uri === parentUri)
        const parentInChain = !parentInReplies
            ? old.parentChain.find(r => r.post.uri === parentUri)
            : undefined
        const parentDepth =
            parentInReplies?.replyDepth ??
            parentInChain?.replyDepth ??
            (old.post.post.uri === parentUri ? -1 : 0)
        const bumpReplyCount = (p: Post): Post =>
            p.uri === parentUri
                ? {
                      ...p,
                      metrics: {
                          ...p.metrics,
                          replyCount: p.metrics.replyCount + 1,
                      },
                  }
                : p
        return {
            ...old,
            post: { ...old.post, post: bumpReplyCount(old.post.post) },
            parentChain: old.parentChain.map(r =>
                r.post.uri === parentUri ? { ...r, post: bumpReplyCount(r.post) } : r
            ),
            replies: [
                ...old.replies.map(r =>
                    r.post.uri === parentUri ? { ...r, post: bumpReplyCount(r.post) } : r
                ),
                { post: reply, replyDepth: parentDepth + 1 },
            ],
            replyCount: old.replyCount + 1,
        }
    }
}
